import React, { useEffect, useMemo, useState } from 'react';
import { WidgetBaseProps } from '../WidgetContainer';
import { useDashboard } from 'routes/dashboard/Dashboard';
import { useTemplateQueries } from 'routes/dashboard/DashboardPageRoot';
import { ActiveWidgetRequest, Widget, WidgetCache, WidgetQuery, WidgetRequest, WidgetRequestInfo, WidgetResponseItem } from 'system/widget/Widget';
import { enqueueSnackbar } from 'notistack';
import APIRequest from 'system/APIRequest';
import QueryWidgetCacheWrapper from './QueryWidgetCacheWrapper';
import { Pageview } from '@mui/icons-material';
import { Tooltip, Box, useTheme, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Query } from 'system/query/Query';
import { QueryInfo } from 'system/query/QueryInfo';

export interface WithQueryWidgetSettings {
  hasSettingsDialog?: boolean,
  hasQueryDialog?: boolean,
  hideImageExport?: boolean,
  hideDataExports?: boolean,
  exportOptions?: string[],
  showPDFExport?: boolean,
}

export interface WithQueryWidgetProps extends WidgetBaseProps {
  widgetBaseSettings?: WithQueryWidgetSettings;
  WrappedComponent?: React.ComponentType<any>;

  saveWidgetData: (widgetData: any, overwrite?: boolean) => Promise<void>;
  saveWidgetDataAndRequests: (widgetData: any, widgetRequests: WidgetRequest[] | undefined, overwriteExistingData?: boolean) => Promise<void>;
  saveWidgetQueries: (widgetQueries: WidgetQuery[] | undefined) => Promise<void>;
  saveWidgetRequests: (widgetRequests: WidgetRequest[] | undefined) => Promise<void>;
  saveWidgetDataCache: (WidgetCache: WidgetCache) => Promise<void>;

  usingCache?: boolean;

  widgetQueries?: Query[];

  // Query info matching the dataset used for the widget queries or template queries driving this widget.
  queryInfo?: QueryInfo;

  activeWidgetRequests?: Array<ActiveWidgetRequest | ActiveWidgetRequest[]>;
  apiResponses?: Array<WidgetResponseItem | WidgetResponseItem[]>;

  widgetRef?: React.RefObject<HTMLDivElement> | null;
}

const defaultWidgetRequestInfo: WidgetRequestInfo = {
  queries: [],
  requests: []
};

const defaultWidgetCache: WidgetCache = {
  cachedAt: Date.now().toString(),
  cacheItems: [],
  cachedQueries: []
};

export interface WithQueryWidgetOptions {
  hasSettingsDialog?: boolean,
  hasQueryDialog?: boolean,
  hideDataExports?: boolean,
  hideImageExport?: boolean,
  exportOptions?: string[],
  enforceOutputAsArray?: boolean,
  showPDFExport?: boolean,
}

// eslint-disable-next-line
export default function withQueryWidget<T extends WidgetBaseProps>(WrappedComponent: React.ComponentType<any>, settings: WithQueryWidgetOptions) {
  return function WithQueryWidget(props: WidgetBaseProps): JSX.Element {
    const dashboard = useDashboard();
    const templateQueriesContext = useTemplateQueries();
    const theme = useTheme();
    const { i18n } = useTranslation();

    const [widget, setWidget] = useState<Widget>(props.widget);
    
    useEffect(() => {
      // If the parent has changed the widget we need to update the reference.  This can happen 
      // with things like "Reload Page Widgets".
      if (props.widget !== widget) {
        // Calling `setWidget` will trigger the load process
        setWidget(props.widget);
      }
    }, [props.widget, props.widget.cache]);
    
    async function save(updatedWidgetInfo:any, isCacheSave: boolean = false) {
      if (!dashboard._modifiable) {
        return Promise.reject(new Error("Dashboard is not modifiable"));
      }
      if (!widget.id || !updatedWidgetInfo) {
        return Promise.reject(new Error("Nothing to update"));
      }
  
      // Ensure we do not send an empty object or other cache data not serialized to a string. The API would get mad.
      let widgetInfo = updatedWidgetInfo;
      if (updatedWidgetInfo.cache && typeof(updatedWidgetInfo.cache) !== "string") {
        widgetInfo = {
          ...updatedWidgetInfo,
          cache: JSON.stringify(updatedWidgetInfo.cache)
        };
      }

      if (!widgetInfo) {
        return;
      }
  
      try {
        await APIRequest(`obj/dashboard-widget/${widget.id}`, {}, {
          method: 'PUT',
          body: JSON.stringify(widgetInfo)
        });
        load(widget.id, isCacheSave);
      } catch (error: any) {
        enqueueSnackbar(`${i18n.t("error")}: ${error.message}`, { variant: "error", persist: true });
      }
    }

    async function load(widgetId: string, isCacheSave: boolean = false) {
      try {
        const response: any = await APIRequest(`obj/dashboard-widget/${widgetId}`);
        const widget: Widget = new Widget(response?.output);
        widget.isCached = isCacheSave;
        setWidget(widget);
      } catch(error: any) {
        // setSaveWidgetError(reason);
        enqueueSnackbar(`${i18n.t("widgets.errors.save-widget-error")} - ${error.message}`, { variant: "error" });
      }
    }

    // Save over the full widget definition. In general, it is safer and easier to
    // use the more specific saveWidget* functions below, so this is not passed to children.
    function saveWidget(updatedWidgetInfo:any, resetPage:boolean = false):Promise<any> {
      return save(updatedWidgetInfo);
    }

    function saveWidgetData(widgetData: any, overwriteExistingData?: boolean) : Promise<void> {
      // Save the data to the widget.
      const newData = overwriteExistingData ? widgetData : { ...widget.data, ...widgetData };
      return saveWidget({ data: newData }, true);
    }

    function saveWidgetDataAndRequests(widgetData: any, widgetRequests: WidgetRequest[] | undefined, overwriteExistingData?: boolean) : Promise<void> {
      const newData = overwriteExistingData ? widgetData : { ...widget.data, ...widgetData };
      // Save the request info to the widget
      const oldRequestInfo = widget.request_info || defaultWidgetRequestInfo;
      let requestInfoToSave = {
        ...oldRequestInfo,
        requests: widgetRequests || [],
      };
      requestInfoToSave = JSON.parse(JSON.stringify(requestInfoToSave));
      return saveWidget({ data: newData, request_info: requestInfoToSave, cache: {} }, true);
    }

    function saveWidgetRequests(widgetRequests: WidgetRequest[] | undefined) : Promise<void> {
      // Save the request info to the widget
      const oldRequestInfo = widget.request_info || defaultWidgetRequestInfo;
      let requestInfoToSave = {
        ...oldRequestInfo,
        requests: widgetRequests || [],
      };
      requestInfoToSave = JSON.parse(JSON.stringify(requestInfoToSave));
      return saveWidget({ request_info: requestInfoToSave, cache: {} }, true);
    }

    function saveWidgetQueries(widgetQueries: WidgetQuery[] | undefined) {
      // Save the queries to the widget
      const oldRequestInfo = widget.request_info || defaultWidgetRequestInfo;
      let requestInfoToSave = {
        ...oldRequestInfo,
        queries: widgetQueries || [],
      };
      requestInfoToSave = JSON.parse(JSON.stringify(requestInfoToSave));
      return saveWidget({ request_info: requestInfoToSave, cache: null }, true);
    }

    function saveWidgetDataCache(widgetCache?: WidgetCache) {
      // Save a cache item to the widget's cache
      return save({ cache: widgetCache }, true);
    }

    const needsTemplateQuery = useMemo(() => {
      return dashboard?.template_type && (!templateQueriesContext?.selectedTemplateQueries || !templateQueriesContext?.selectedTemplateQueries.length);
    }, [dashboard?.template_type, templateQueriesContext?.selectedTemplateQueries]);

    return (
      <>
      {/* If we are in a template and no template queries are provided, show a warning */}
      {needsTemplateQuery &&
        <>
        <Typography variant="h4" align="center" sx={{ mt: 1, fontWeight: "normal", opacity: "0.5" }}>
          {i18n.t(`widget-types.${widget?.name}.label`)}
        </Typography>

        <Box sx={{position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", alignItems: "center", justifyContent: "center"}}>
          <Tooltip title={i18n.t("widgets.template-widget-missing-query")}>
            <Pageview sx={{ fontSize: "5rem", color: theme.colors.secondary.main, opacity: "0.15" }} />
          </Tooltip>
        </Box>
        </>
      }
      {!needsTemplateQuery &&
        <QueryWidgetCacheWrapper
          {...props}
          widget={widget}
          widgetBaseSettings={settings}
          WrappedComponent={WrappedComponent}
          saveWidgetData={saveWidgetData}
          saveWidgetDataAndRequests={saveWidgetDataAndRequests}
          saveWidgetQueries={saveWidgetQueries}
          saveWidgetRequests={saveWidgetRequests}
          saveWidgetDataCache={saveWidgetDataCache}
        >
        </QueryWidgetCacheWrapper>
      }
    </>
  );
};
}
